import { SignUpFormData } from '@pages/SignUp/SignUp.types';
import { SignUpInvitesOnly } from '@utils/environmentVariables/environmentVariables';
import {
  middleNameRegex,
  nameRegex,
  passwordRegex,
  reqLowerRegex,
  reqNumbersRegex,
  reqUpperRegex,
  specialCharsRegex,
} from '@utils/validation/consts';
import moment from 'moment';
import * as yup from 'yup';

yup.setLocale({
  mixed: {
    required: ({ path }) => `signUp.errors.${path}.required`,
  },
  string: {
    matches: ({ path }) => `signUp.errors.${path}.matches`,
  },
});

export const signUpFormSchema = yup
  .object<Record<keyof SignUpFormData, yup.AnySchema>>({
    residenceCountry: yup.string().trim().required().nullable(),
    firstName: yup.string().required().trim().matches(nameRegex),
    middleName: yup.string().matches(middleNameRegex),
    surname: yup.string().required().trim().matches(nameRegex),
    birthDate: yup
      .string()
      .required()
      .typeError('signUp.errors.birthDate.invalidDate')
      .nullable()
      .test(
        'birthDate min invalid',
        'signUp.errors.birthDate.invalidDate',
        (value) => {
          if (!moment(value, false).isValid()) {
            return false;
          }
          return (
            !moment(value)
              .format('DD-MM-YYYY')
              .split('-')
              .some((x) => Number(x) <= 0) && Number(value?.split(' ')[3]) !== 0
          );
        }
      )
      .test('birthDate min age', 'signUp.errors.birthDate.minAge', (value) => {
        return moment().diff(moment(value), 'years') >= 18;
      }),
    emailAddress: yup
      .string()
      .email('signUp.errors.emailAddress.format')
      .required(),
    password: yup
      .string()
      .required()
      .test({
        name: 'password_validation',
        test: (value, { createError }) => {
          if (!value) return false;

          const errors = [];
          if (!reqLowerRegex.test(value)) errors.push('lowercase');
          if (!reqUpperRegex.test(value)) errors.push('uppercase');
          if (!reqNumbersRegex.test(value)) errors.push('digits');
          if (!specialCharsRegex.test(value)) errors.push('specialChars');
          if (value.length < 8) errors.push('length');

          return errors.length
            ? createError({
                message: errors.join('.'),
                path: 'password',
              })
            : true;
        },
      })
      .matches(passwordRegex, 'signUp.errors.password.invalidCharacters'),
    confirmPassword: yup
      .string()
      .when('password', {
        is: (val: string) => (val && val.length > 0 ? true : false),
        then: yup
          .string()
          .oneOf(
            [yup.ref('password')],
            'signUp.errors.confirmPassword.different'
          ),
      })
      .required(),
    hasAcceptedTerms: yup.bool().oneOf([true]),
    isQualifiedInvestor: yup.bool().oneOf([true]),
    agreeToMarketingCommunications: yup.boolean().notRequired(),
    inviteCode: yup.string().when([], {
      is: () => SignUpInvitesOnly === false,
      then: yup.string().nullable(),
      otherwise: yup.string().trim().required(),
    }),
  })
  .required();
